import React, { memo } from 'react';
import { Box, Checkbox, Typography } from '@mui/material';
import { TreeItem } from '@mui/x-tree-view';

// Form values interface for Formik
export interface AccountsFormValues {
  excludedAccounts: number[];
  excludedCategories: number[];
}

// Tree structure types
export interface TreeStructure {
  id: string;
  name: string;
  classificationId: number;
  accounts: Array<{
    id: string;
    name: string;
    accountId: number;
  }>;
}

interface AccountTreeItemProps {
  account: {
    id: string;
    name: string;
    accountId: number;
  };
  initialIsExcluded: boolean;
  onAccountToggle: (accountId: number) => void;
}

interface CategoryTreeItemProps {
  category: TreeStructure;
  initialExcludedChildrenCount: number;
  totalChildren: number;
  initialExcludedAccounts: number[];
  onAccountToggle: (accountId: number) => void;
  onCategoryToggle: (classificationId: number, accountIds: number[]) => void;
}

interface RenderTreeProps {
  treeStructure: TreeStructure[];
  initialFormikValues: AccountsFormValues;
  onAccountToggle: (accountId: number) => void;
  onCategoryToggle: (classificationId: number, accountIds: number[]) => void;
}

const AccountTreeItem = memo(
  ({ account, initialIsExcluded, onAccountToggle }: AccountTreeItemProps) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      onAccountToggle(account.accountId);
    };

    return (
      <TreeItem
        key={account.id}
        itemId={account.id}
        label={
          <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
            gap={1}
          >
            <Typography variant='body2'>{account.name}</Typography>
            <Checkbox
              id={`excludedAccounts-${account.accountId}`}
              name='excludedAccounts'
              checked={!initialIsExcluded}
              onChange={handleChange}
              onClick={(e) => {
                e.stopPropagation();
              }}
              size='small'
            />
          </Box>
        }
      />
    );
  },
);
AccountTreeItem.displayName = 'AccountTreeItem';

const CategoryTreeItem = memo(
  ({
    category,
    initialExcludedChildrenCount,
    totalChildren,
    initialExcludedAccounts,
    onAccountToggle,
    onCategoryToggle,
  }: CategoryTreeItemProps) => {
    // Calculate checkbox state based on children:
    // - If all children are included (excludedChildrenCount === 0) → checked
    // - If all children are excluded (excludedChildrenCount === totalChildren) → unchecked
    // - If some children are excluded (0 < excludedChildrenCount < totalChildren) → indeterminate
    const isChecked = initialExcludedChildrenCount === 0;
    const isIndeterminate =
      initialExcludedChildrenCount > 0 &&
      initialExcludedChildrenCount < totalChildren;

    // Get all account IDs in this category
    const accountIds = category.accounts.map((account) => account.accountId);

    const handleCategoryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      // Toggle all children accounts when category checkbox is clicked
      onCategoryToggle(category.classificationId, accountIds);
    };

    return (
      <TreeItem
        key={category.id}
        itemId={category.id}
        label={
          <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
            gap={1}
          >
            <Typography variant='body2'>{category.name}</Typography>
            <Checkbox
              checked={isChecked}
              indeterminate={isIndeterminate}
              onChange={handleCategoryChange}
              onClick={(e) => {
                e.stopPropagation();
              }}
              size='small'
            />
          </Box>
        }
      >
        {category.accounts.map((account) => {
          const isAccountExcluded = initialExcludedAccounts.includes(
            account.accountId,
          );
          return (
            <AccountTreeItem
              key={account.id}
              account={account}
              initialIsExcluded={isAccountExcluded}
              onAccountToggle={onAccountToggle}
            />
          );
        })}
      </TreeItem>
    );
  },
);
CategoryTreeItem.displayName = 'CategoryTreeItem';

// Render tree - re-renders when Formik values change to update checkbox states
export const RenderTree = memo(
  ({
    treeStructure,
    initialFormikValues,
    onAccountToggle,
    onCategoryToggle,
  }: RenderTreeProps) => {
    if (!treeStructure.length) return null;

    return (
      <>
        {treeStructure.map((category) => {
          // Calculate current state from Formik values
          const excludedChildrenCount = category.accounts.filter((account) =>
            initialFormikValues.excludedAccounts.includes(account.accountId),
          ).length;
          const totalChildren = category.accounts.length;

          return (
            <CategoryTreeItem
              key={category.id}
              category={category}
              initialExcludedChildrenCount={excludedChildrenCount}
              totalChildren={totalChildren}
              initialExcludedAccounts={initialFormikValues.excludedAccounts}
              onAccountToggle={onAccountToggle}
              onCategoryToggle={onCategoryToggle}
            />
          );
        })}
      </>
    );
  },
  (prevProps, nextProps) => {
    // Re-render when Formik values change to update checkbox states
    // Compare arrays by reference - if they're different, re-render
    return (
      prevProps.treeStructure === nextProps.treeStructure &&
      prevProps.initialFormikValues.excludedAccounts ===
        nextProps.initialFormikValues.excludedAccounts &&
      prevProps.initialFormikValues.excludedCategories ===
        nextProps.initialFormikValues.excludedCategories &&
      prevProps.onAccountToggle === nextProps.onAccountToggle &&
      prevProps.onCategoryToggle === nextProps.onCategoryToggle
    );
  },
);
RenderTree.displayName = 'RenderTree';
